import type { RaRecord } from "ra-core";
import {
  useInfinitePaginationContext,
  useListContext,
  useTranslate,
} from "ra-core";
import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import {
  Virtuoso,
  type GridStateSnapshot,
  type StateSnapshot,
  type VirtuosoHandle,
} from "react-virtuoso";

import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";

import { Spinner } from "@/components/admin/spinner";
import { Item, ItemContent, ItemMedia, ItemTitle } from "@/components/ui/item";
import { ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

type ScrollSnapshot = StateSnapshot | GridStateSnapshot;

// Minimal shape needed to capture a scroll snapshot imperatively. VirtuosoHandle
// declares getState; VirtuosoGridHandle does NOT, even though the grid implements it
// (react-virtuoso's own runtime method map lists getState for the grid, and the
// package exports GridStateSnapshot, which nothing else could produce). The grid side
// therefore declares the method locally — see VirtualizedGridContent.
export interface VirtuosoStateHandle {
  getState(cb: (snapshot: ScrollSnapshot) => void): void;
}

// Session-scoped scroll snapshots, keyed by list identity (resource + filters +
// sort). Kept OUTSIDE the component so it survives the virtualized list's
// unmount when you open a detail page — returning re-mounts the list and reads
// its snapshot back. In-memory only: cleared on a hard reload, which matches the
// react-query page cache whose retained pages make a restored scrollTop land on
// real rows rather than an unloaded void.
const scrollSnapshots = new Map<string, ScrollSnapshot>();

/**
 * Scroll-position restoration for a virtualized infinite list. Wire the three
 * returned values into <Virtuoso>/<VirtuosoGrid>: `ref`, `restoreStateFrom`
 * (initial-only — captured once at mount for the current view's key), and
 * `isScrolling` (captures a fresh snapshot each time scrolling settles).
 *
 * Keying by resource+filters+sort means the same view restores while a filter
 * or sort change is a different key → the list correctly starts back at the top.
 */
export const useVirtuosoScrollRestoration = <
  H extends VirtuosoStateHandle,
>() => {
  const { resource, filterValues, sort } = useListContext();
  const key = JSON.stringify({ resource, filterValues, sort });
  const virtuosoRef = useRef<H>(null);
  // Frozen at mount: restoreStateFrom is an initial-only prop, and the mount-time
  // key reflects the view we're returning to.
  const [restoreStateFrom] = useState(() => scrollSnapshots.get(key));
  const handleIsScrolling = useCallback(
    (isScrolling: boolean) => {
      if (isScrolling) return; // capture only when scrolling settles
      virtuosoRef.current?.getState((snapshot) => {
        scrollSnapshots.set(key, snapshot);
      });
    },
    [key],
  );
  return { virtuosoRef, restoreStateFrom, handleIsScrolling };
};

/**
 * Virtuoso `components.Scroller` override that renders the themed
 * <ScrollArea>, so virtualized lists get the exact shadcn overlay scrollbar.
 * Virtuoso's forwarded ref lands on the Radix viewport — the element that
 * actually scrolls — so Virtuoso keeps its normal measurement path (its own
 * ResizeObserver on its own scroller). That matters for hidden tabs: Chrome
 * suspends RO/rAF while a tab is hidden (a list mounted there renders zero
 * rows), and the queued RO observation delivers as soon as the tab becomes
 * visible, so the list self-recovers. Do NOT switch to `customScrollParent`
 * — that path recomputes only on window events and can stay blank instead.
 */
export const VirtuosoScroller = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ style, children, className, ...props }, ref) => (
  // Virtuoso treats the element carrying its forwarded ref/style/props as THE
  // scroller: the style includes overflow, the props include its scroll
  // handlers, and the ref is what it measures. All three MUST land on the
  // same element — the Radix Viewport, which is the element that actually
  // scrolls (splitting them across Root/Viewport froze the render window:
  // scroll events fired on a non-scrolling element). "relative" makes the
  // viewport the containing block for Virtuoso's absolutely positioned inner
  // container, so the virtual height creates scroll overflow here.
  <ScrollAreaPrimitive.Root
    data-slot="scroll-area"
    className={cn("relative flex-1 min-h-0", className)}
  >
    <ScrollAreaPrimitive.Viewport
      ref={ref}
      data-slot="scroll-area-viewport"
      className="relative size-full rounded-[inherit] outline-none"
      style={style}
      {...props}
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
VirtuosoScroller.displayName = "VirtuosoScroller";

/**
 * Keeps the Radix ScrollArea content wrapper's height in sync with Virtuoso's
 * total virtual height. Virtuoso's inner container is absolutely positioned,
 * so the wrapper never resizes on its own — and Radix sizes the scrollbar
 * thumb from a ResizeObserver on that wrapper. Without this sync the thumb
 * length goes stale as pages load (it recomputes only when the scrollbar
 * remounts on hover). Wire the returned props onto Virtuoso/VirtuosoGrid.
 */
export const useSyncRadixThumbHeight = () => {
  const contentRef = useRef<HTMLElement | null>(null);
  const scrollerRef = useCallback((el: HTMLElement | Window | null) => {
    // el is the Radix viewport; its first child is Radix's content wrapper.
    contentRef.current =
      el instanceof HTMLElement
        ? (el.firstElementChild as HTMLElement | null)
        : null;
  }, []);
  const totalListHeightChanged = useCallback((height: number) => {
    if (contentRef.current) {
      contentRef.current.style.height = `${height}px`;
    }
  }, []);
  return { scrollerRef, totalListHeightChanged };
};

/**
 * Grid variant of the thumb-height sync: VirtuosoGrid has NO
 * `totalListHeightChanged` prop (only linear Virtuoso does — passing it is
 * silently ignored), so instead watch the grid's item-list element with a
 * border-box ResizeObserver. VirtuosoGrid windows via padding-bottom on that
 * element, and border-box observation fires on padding changes; stamp the
 * resulting full virtual height onto Radix's content wrapper so the thumb
 * tracks loaded content.
 */
export const useSyncRadixThumbHeightFromGrid = () => {
  const cleanupRef = useRef<(() => void) | null>(null);
  const scrollerRef = useCallback((el: HTMLElement | Window | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!(el instanceof HTMLElement)) return;
    const wrapper = el.firstElementChild as HTMLElement | null;
    const itemList = el.querySelector<HTMLElement>(
      '[data-testid="virtuoso-item-list"]',
    );
    if (!wrapper || !itemList) return;
    const sync = () => {
      wrapper.style.height = `${itemList.offsetTop + itemList.offsetHeight}px`;
    };
    const observer = new ResizeObserver(sync);
    observer.observe(itemList, { box: "border-box" });
    sync();
    cleanupRef.current = () => observer.disconnect();
  }, []);
  return { scrollerRef };
};

/**
 * Virtualized body for infinite lists: renders only the visible rows
 * (react-virtuoso, auto-measured heights) and fetches the next page when the
 * user nears the bottom.
 *
 * Must live under an InfiniteListBase (e.g. via <InfiniteList>) — rows come
 * from ListContext, paging from InfinitePaginationContext. Scrolling happens
 * in the themed <ScrollArea> via the VirtuosoScroller override.
 */
export const VirtualizedListContent = <RecordType extends RaRecord = RaRecord>({
  itemContent,
  increaseViewportBy = 600,
  className,
}: VirtualizedListContentProps<RecordType>) => {
  const { data, isPending } = useListContext<RecordType>();
  const { fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfinitePaginationContext();
  const { scrollerRef, totalListHeightChanged } = useSyncRadixThumbHeight();
  const { virtuosoRef, restoreStateFrom, handleIsScrolling } =
    useVirtuosoScrollRestoration<VirtuosoHandle>();

  if (!fetchNextPage) {
    throw new Error(
      "VirtualizedListContent must be used inside an InfinitePaginationContext, usually created by <InfiniteList>. You cannot use it as child of a <List> component.",
    );
  }

  if (isPending || !data) return null;

  return (
    <Virtuoso
      ref={virtuosoRef}
      restoreStateFrom={restoreStateFrom as StateSnapshot | undefined}
      isScrolling={handleIsScrolling}
      className={cn("flex-1 min-h-0", className)}
      style={{ height: "100%" }}
      data={data}
      computeItemKey={(_, record) => record.id}
      endReached={() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      }}
      increaseViewportBy={{ top: 0, bottom: increaseViewportBy }}
      components={{ Scroller: VirtuosoScroller, Footer: LoadingFooter }}
      itemContent={itemContent}
      scrollerRef={scrollerRef}
      totalListHeightChanged={totalListHeightChanged}
    />
  );
};

/**
 * Spinner row shown below the list while the next page loads. Shared with
 * VirtualizedGridContent.
 */
export const LoadingFooter = () => {
  const translate = useTranslate();
  const { hasNextPage, isFetchingNextPage } = useInfinitePaginationContext();

  if (!hasNextPage || !isFetchingNextPage) return null;

  return (
    <div className="py-2 text-center">
      <Item variant="default">
        <ItemMedia>
          <Spinner />
        </ItemMedia>
        <ItemContent>
          <ItemTitle className="line-clamp-1">
            {translate("crm.common.loading")}
          </ItemTitle>
        </ItemContent>
      </Item>
    </div>
  );
};

export interface VirtualizedListContentProps<
  RecordType extends RaRecord = RaRecord,
> {
  /** Row renderer — receives the row index and the record. */
  itemContent: (index: number, record: RecordType) => ReactNode;
  /** Extra pixels rendered beyond the viewport bottom (scroll look-ahead). */
  increaseViewportBy?: number;
  className?: string;
}
